import Cropper from 'cropperjs';
import { fetchImageObjectUrl } from '../../../imageFetchUtils';
import { reflowCropperAfterTransforms } from './cropperUtils';

// Matches the JPEG/WebP export quality already used elsewhere in the image editor flow.
// We keep this slightly below 1.0 to avoid oversized uploads without a visible quality drop.
export const DEFAULT_LOSSY_IMAGE_QUALITY = 0.85;
const CROPPER_RESTRICT_TO_CANVAS_VIEW_MODE = 2;

type CroppedImageBlobOptions = {
    imageUrl: string;
    inputImageType: string;
    outputImageType: string;
    imageEditorData: Cropper.Data;
};

type CroppedImageBlobFromBlobOptions = {
    imageBlob: Blob;
    outputImageType: string;
    imageEditorData: Cropper.Data;
};

/**
 * Draw an image object URL into a canvas so the editor can read a data URL for Cropper.
 */
export const loadImageIntoCanvas = (objectUrl: string, imageType: string): Promise<{ dataUrl: string }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Unable to load image into canvas'));
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL(imageType);

            resolve({ dataUrl });
        };

        img.onerror = reject;
        img.src = objectUrl;
        img.alt = 'To edit';
    });
};

/**
 * Create hidden DOM nodes for Cropper, which needs a mounted image to calculate crop bounds.
 */
const createOffscreenContainer = (): { container: HTMLDivElement; image: HTMLImageElement } => {
    const container = document.createElement('div');
    // Keep the image mounted for Cropper measurements without ever showing it in the viewport.
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '-10000px';

    const image = document.createElement('img');
    container.appendChild(image);
    document.body.appendChild(container);

    return { container, image };
};

/**
 * Load an image source into an existing image element before Cropper reads its dimensions.
 */
const loadImageSourceIntoImage = (image: HTMLImageElement, imageSource: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = reject;
        image.src = imageSource;
    });
};

/**
 * Apply saved editor crop data to a mounted image and export the cropped result as a blob.
 */
const cropImageToBlob = (
    image: HTMLImageElement,
    imageEditorData: Cropper.Data,
    outputImageType: string,
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const cropper = new Cropper(image, {
            // Keep the crop selection fully inside the rendered image bounds.
            viewMode: CROPPER_RESTRICT_TO_CANVAS_VIEW_MODE,
            autoCrop: true,
            autoCropArea: 1,
            checkOrientation: false,
            ready: () => {
                const cropSelection = {
                    x: imageEditorData.x,
                    y: imageEditorData.y,
                    width: imageEditorData.width,
                    height: imageEditorData.height,
                };
                cropper.setData(imageEditorData);
                reflowCropperAfterTransforms(cropper, cropSelection);
                const croppedData = cropper.getData();
                cropper
                    .getCroppedCanvas({
                        width: croppedData.width,
                        height: croppedData.height,
                        maxWidth: 4096,
                        maxHeight: 4096,
                    })
                    .toBlob(
                        (blob: Blob | null) => {
                            cropper.destroy();
                            if (!blob) return reject(new Error('Failed to produce cropped image blob'));
                            resolve(blob);
                        },
                        outputImageType,
                        DEFAULT_LOSSY_IMAGE_QUALITY,
                    );
            },
        });
    });
};

/**
 * Download an image, mount it offscreen, and return the saved crop area as a new image blob.
 */
export const getCroppedImageBlobFromUrl = async ({
    imageUrl,
    inputImageType,
    outputImageType,
    imageEditorData,
}: CroppedImageBlobOptions): Promise<Blob> => {
    const { objectUrl } = await fetchImageObjectUrl(imageUrl, inputImageType);
    const { container, image } = createOffscreenContainer();

    try {
        await loadImageSourceIntoImage(image, objectUrl);
        return await cropImageToBlob(image, imageEditorData, outputImageType);
    } finally {
        URL.revokeObjectURL(objectUrl);
        container.remove();
    }
};

/** Crops an already-available image blob without routing it through the provider or upload service. */
export const getCroppedImageBlobFromBlob = async ({
    imageBlob,
    outputImageType,
    imageEditorData,
}: CroppedImageBlobFromBlobOptions): Promise<Blob> => {
    const objectUrl = URL.createObjectURL(imageBlob);
    const { container, image } = createOffscreenContainer();

    try {
        await loadImageSourceIntoImage(image, objectUrl);
        return await cropImageToBlob(image, imageEditorData, outputImageType);
    } finally {
        URL.revokeObjectURL(objectUrl);
        container.remove();
    }
};

export const loadHtmlImageFromBlob = async (blob: Blob): Promise<{ image: HTMLImageElement; revoke: () => void }> => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    try {
        await loadImageSourceIntoImage(image, objectUrl);
    } catch (error) {
        URL.revokeObjectURL(objectUrl);
        throw error;
    }

    return { image, revoke: () => URL.revokeObjectURL(objectUrl) };
};

/**
 * Scale an image blob to exactly match the reference blob's pixel dimensions (no-op when they already do).
 *
 * The cross-variant background-removal maths — reusing editor data across variants, mirroring crops onto
 * the hidden variant, and the drawing scale derived from `imageOriginal` — all require the with/without
 * background sources to be pixel-aligned. The provider can return output sized differently to the image it
 * was sent, so its result is conformed back onto the source's grid before anything is cached.
 */
export const conformImageBlobDimensions = async ({
    imageBlob,
    referenceBlob,
}: {
    imageBlob: Blob;
    referenceBlob: Blob;
}): Promise<Blob> => {
    const reference = await loadHtmlImageFromBlob(referenceBlob);
    const target = await loadHtmlImageFromBlob(imageBlob);

    try {
        const { naturalWidth, naturalHeight } = reference.image;
        if (target.image.naturalWidth === naturalWidth && target.image.naturalHeight === naturalHeight) {
            return imageBlob;
        }

        const canvas = document.createElement('canvas');
        canvas.width = naturalWidth;
        canvas.height = naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Unable to resize image blob');

        context.drawImage(target.image, 0, 0, naturalWidth, naturalHeight);

        return await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error('Failed to produce resized image blob'))),
                imageBlob.type,
            );
        });
    } finally {
        reference.revoke();
        target.revoke();
    }
};
